/**
 * `/billing` — what the organisation owes FinderlyFix under the Contractor
 * Fee Schedule (contractor pages phase 2): the balance, the fees and the
 * schedule's acceptance, the statement (filters, "Load more", CSV), the
 * invoices and the billing contact.
 *
 *   * Off (`whoami.features.contractor_billing` false, or the API answers
 *     404): one calm card and nothing else; no billing call is made.
 *   * The schedule (`legal/contractor-fee-schedule.ts`) is read and accepted
 *     inline, the same mechanics as the Partner Terms modal (TermsGate): the
 *     document, a tick box naming the organisation, Accept. It sends
 *     CURRENT_FEE_SCHEDULE.version verbatim; the server refuses any other
 *     with 409 FEE_SCHEDULE_OUTDATED. Owners and admins only.
 *   * Invoices are issued outside the portal by the FinderlyFix contact,
 *     monthly, net 15; this page lists them.
 *
 * `#fee-schedule` (the lead detail's "accept the fee schedule" link) scrolls
 * to the fee card and opens the review for an owner or admin who owes it.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type {
  BillingAccountView,
  BillingOverview,
  InvoiceView,
  LedgerEntryView,
  LedgerFilters,
  LedgerKind,
} from '../api/types';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { billingError } from '../billing/errors';
import { formatBps, formatCents } from '../billing/money';
import { InvoiceStatusTag, LEDGER_KINDS, LedgerTable, ledgerKindLabel, invoiceName, periodLabel } from '../billing/parts';
import { CONTRACTOR_FEE_SCHEDULE_DOCUMENT } from '../legal/contractor-fee-schedule';
import { BILLING_EMAIL, CURRENT_FEE_SCHEDULE, FEE_SCHEDULE_PATH, legalVersionLabel } from '../legal/current';
import { LegalDocumentView } from '../legal/LegalDocumentView';
import { useUnsavedChangesGuard } from '../router';
import { Disclosure, Empty, ErrorNotice, Field, Loading, PageHead, formatDate, formatDateTime, humanize, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

export const FEE_SCHEDULE_ANCHOR = 'fee-schedule';
const PAGE_SIZE = 50;

export function Billing() {
  const { whoami } = useSession();
  if (whoami?.features?.contractor_billing !== true) return <BillingOff />;
  return <BillingOn />;
}

function BillingOff() {
  return (
    <>
      <PageHead title="Billing" />
      <section className="panel panel-pad stack-sm" aria-labelledby="billing-off-h">
        <h2 id="billing-off-h" className="h-sub">
          Billing isn’t switched on for your organisation yet
        </h2>
        <p className="muted" style={{ margin: 0 }}>
          Nothing is charged through the portal. If FinderlyFix switches on pay-per-lead billing for you, your fees, statement and invoices
          will appear here, and nothing is charged until an owner or admin accepts the Contractor Fee Schedule.
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          Questions about billing: <a href={`mailto:${BILLING_EMAIL}`}>{BILLING_EMAIL}</a>.
        </p>
      </section>
    </>
  );
}

function BillingOn() {
  const { api } = useSession();
  const overview = useQuery(() => api.getBilling(), ['billing']);

  if (overview.loading && !overview.data) return <Loading label="Loading billing" />;
  if (overview.error?.status === 404 || overview.data?.enabled === false) return <BillingOff />;
  if (overview.error || !overview.data) {
    return (
      <>
        <PageHead title="Billing" />
        <ErrorNotice error={billingError(overview.error)} title="Could not load billing" />
      </>
    );
  }
  const data = overview.data;
  return (
    <div>
      <PageHead title="Billing" lede="What your organisation owes FinderlyFix for accepted leads and finished jobs." />
      <div className="stack-lg">
        <BalanceHeader data={data} />
        <div className="grid-2" style={{ alignItems: 'start' }}>
          <FeeCard data={data} onChange={(next) => overview.setData(next)} />
          <BillingContact
            key={data.account ? 'account' : 'none'}
            account={data.account}
            onSaved={(account) => overview.setData({ ...data, account })}
          />
        </div>
        <Statement />
        <Invoices />
      </div>
    </div>
  );
}

// ── Balance ──────────────────────────────────────────────────────────────
function BalanceHeader({ data }: { data: BillingOverview }) {
  return (
    <section className="panel panel-pad stack-sm" aria-labelledby="billing-balance-h">
      <div className="row-between" style={{ alignItems: 'baseline' }}>
        <h2 id="billing-balance-h" className="h-sub" style={{ margin: 0 }}>
          Balance <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCents(data.balance_cents)}</span>
        </h2>
        {data.open_invoice_count ? (
          <span className="tag warn">
            {data.open_invoice_count} open invoice{data.open_invoice_count === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
      <p className="small muted" style={{ margin: 0 }}>
        A positive balance means you owe FinderlyFix; a negative one is a credit. Invoices come from your FinderlyFix contact, monthly, net
        15. You pay them outside the portal.
      </p>
    </section>
  );
}

// ── Fees and the Contractor Fee Schedule ─────────────────────────────────
function FeeCard({ data, onChange }: { data: BillingOverview; onChange: (next: BillingOverview) => void }) {
  const { api, org, role } = useSession();
  const toast = useToast();
  const fs = data.fee_schedule;
  const account = data.account;
  const acceptedCurrent = fs.accepted_version !== null && fs.accepted_version === fs.current_version;
  const owed = fs.required || !acceptedCurrent;
  const canAccept = can(role, 'acceptFeeSchedule');
  const [reviewing, setReviewing] = useState(false);
  const [ticked, setTicked] = useState(false);
  const accept = useMutation(() => api.acceptFeeSchedule(CURRENT_FEE_SCHEDULE.version));
  const ref = useRef<HTMLElement>(null);
  const panelId = useId();
  const checkboxId = useId();
  const overrides = Object.entries(account?.lead_fee_by_trade ?? {}).filter(([, cents]) => typeof cents === 'number');
  const charging = Boolean(account && account.billing_model === 'contractor_ppl' && account.fees_enabled);

  useEffect(() => {
    if (window.location.hash !== `#${FEE_SCHEDULE_ANCHOR}`) return;
    ref.current?.scrollIntoView?.({ block: 'start' });
    if (owed && canAccept) setReviewing(true);
    // Once, when the card first renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    const next = await accept.run();
    if (!next) return;
    onChange(next);
    setReviewing(false);
    setTicked(false);
    toast('Contractor Fee Schedule accepted');
  };

  return (
    <section id={FEE_SCHEDULE_ANCHOR} ref={ref} tabIndex={-1} className="panel" aria-labelledby="billing-fees-h">
      <div className="panel-head">
        <h2 id="billing-fees-h">Your fees</h2>
        {acceptedCurrent && !fs.required ? <span className="tag ok">Fee schedule accepted</span> : <span className="tag warn">Not accepted</span>}
      </div>
      <div className="panel-body stack-sm">
        <dl className="facts">
          <dt>Per accepted lead</dt>
          <dd>
            {formatCents(fs.lead_fee_cents)}
            {overrides.length ? <span className="muted"> unless the trade has its own price</span> : null}
          </dd>
          {overrides.map(([trade, cents]) => (
            <FeeOverride key={trade} trade={trade} cents={cents} />
          ))}
          <dt>Success fee</dt>
          <dd>{formatBps(fs.success_fee_bps)} of the accepted quote, once the homeowner confirms the job is finished</dd>
          <dt>Fee schedule</dt>
          <dd>
            Version of {legalVersionLabel(fs.current_version)} ·{' '}
            <a href={fs.path || FEE_SCHEDULE_PATH} target="_blank" rel="noreferrer">
              Read it<span className="visually-hidden"> (Contractor Fee Schedule, opens in a new tab)</span>
            </a>
          </dd>
          <dt>Accepted</dt>
          <dd>
            {acceptedCurrent && account?.fee_schedule_accepted_at ? (
              formatDateTime(account.fee_schedule_accepted_at)
            ) : acceptedCurrent ? (
              'Yes'
            ) : fs.accepted_version ? (
              <>An earlier version ({legalVersionLabel(fs.accepted_version)}); the current one isn’t accepted yet</>
            ) : (
              'Not yet'
            )}
          </dd>
        </dl>
        <p className="small muted" style={{ margin: 0 }}>
          You see a lead’s job and area before you accept it. Declining or ignoring a lead costs nothing, and so do test leads.
        </p>
        {!charging ? (
          <p className="notice info small" style={{ margin: 0 }}>
            <span>FinderlyFix hasn’t switched fees on for your organisation yet, so nothing is being charged.</span>
          </p>
        ) : null}
        {owed ? (
          canAccept ? (
            <div>
              <button
                type="button"
                className={reviewing ? 'btn btn-sm' : 'btn btn-primary btn-sm'}
                aria-expanded={reviewing}
                aria-controls={panelId}
                onClick={() => setReviewing(!reviewing)}
              >
                Review and accept the fee schedule
              </button>
            </div>
          ) : (
            <p className="small muted" style={{ margin: 0 }}>
              An owner or admin needs to accept the Contractor Fee Schedule before fees apply and before leads can be accepted.
            </p>
          )
        ) : null}
        <div id={panelId} hidden={!reviewing} className="stack-sm">
          {reviewing ? (
            <>
              <div
                className="legal"
                tabIndex={0}
                role="region"
                aria-label="Contractor Fee Schedule text"
                style={{
                  maxHeight: 360,
                  overflowY: 'auto',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-sm)',
                  padding: 'var(--s4)',
                  background: 'var(--panel-sunk)',
                }}
              >
                <LegalDocumentView doc={CONTRACTOR_FEE_SCHEDULE_DOCUMENT} />
              </div>
              <div className="check">
                <input id={checkboxId} type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
                <label htmlFor={checkboxId}>
                  I have read and agree to the Contractor Fee Schedule on behalf of {org?.display_name ?? 'my organisation'}, and I am
                  authorised to do so.
                </label>
              </div>
              <ErrorNotice error={billingError(accept.error)} />
              <div className="row">
                <button type="button" className="btn btn-primary" disabled={!ticked || accept.pending} onClick={() => void submit()}>
                  {accept.pending ? 'Saving…' : 'Accept the fee schedule'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setReviewing(false);
                    setTicked(false);
                    accept.reset();
                  }}
                >
                  Not now
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function FeeOverride({ trade, cents }: { trade: string; cents: number }) {
  return (
    <>
      <dt className="small" style={{ paddingLeft: 12 }}>
        {humanize(trade)}
      </dt>
      <dd className="small">{formatCents(cents)} per lead</dd>
    </>
  );
}

// ── Statement ────────────────────────────────────────────────────────────
interface StatementFilters {
  kind: LedgerKind | '';
  from: string;
  to: string;
}

function cleanFilters(f: StatementFilters): LedgerFilters {
  return {
    ...(f.kind ? { kind: f.kind } : {}),
    ...(f.from ? { from: f.from } : {}),
    ...(f.to ? { to: f.to } : {}),
  };
}

function Statement() {
  const { api } = useSession();
  const [filters, setFilters] = useState<StatementFilters>({ kind: '', from: '', to: '' });
  const clean = cleanFilters(filters);
  const key = JSON.stringify(clean);
  const first = useQuery(() => api.billingLedger({ ...clean, limit: PAGE_SIZE }), ['billing-ledger', key]);
  const [extra, setExtra] = useState<{ key: string; items: LedgerEntryView[]; cursor: string | null } | null>(null);
  const more = useMutation((cursor: string) => api.billingLedger({ ...clean, limit: PAGE_SIZE }, cursor));
  const csv = useMutation(async () => {
    const blob = await api.downloadBillingLedgerCsv(clean);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'finderlyfix-statement.csv';
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
    return true;
  });
  const mine = extra && extra.key === key ? extra : null;
  const items = [...(first.data?.items ?? []), ...(mine?.items ?? [])];
  const cursor = mine ? mine.cursor : (first.data?.next_cursor ?? null);
  const filtered = Boolean(filters.kind || filters.from || filters.to);
  const set = (patch: Partial<StatementFilters>) => setFilters((f) => ({ ...f, ...patch }));
  const kindId = useId();
  const fromId = useId();
  const toId = useId();

  return (
    <section className="panel" aria-labelledby="billing-statement-h">
      <div className="panel-head">
        <h2 id="billing-statement-h">Statement</h2>
        <button type="button" className="btn btn-sm" onClick={() => void csv.run()} disabled={csv.pending}>
          {csv.pending ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>
      <div className="panel-head" style={{ justifyContent: 'flex-start' }}>
        <label className="row small" style={{ gap: 6 }} htmlFor={kindId}>
          <span className="label">Kind</span>
          <select
            id={kindId}
            className="input"
            style={{ minHeight: 30, width: 'auto' }}
            value={filters.kind}
            onChange={(e) => set({ kind: e.target.value as LedgerKind | '' })}
          >
            <option value="">All</option>
            {LEDGER_KINDS.map((k) => (
              <option key={k} value={k}>
                {ledgerKindLabel(k)}
              </option>
            ))}
          </select>
        </label>
        <label className="row small" style={{ gap: 6 }} htmlFor={fromId}>
          <span className="label">From</span>
          <input
            id={fromId}
            type="date"
            className="input"
            style={{ minHeight: 30, width: 'auto' }}
            value={filters.from}
            max={filters.to || undefined}
            onChange={(e) => set({ from: e.target.value })}
          />
        </label>
        <label className="row small" style={{ gap: 6 }} htmlFor={toId}>
          <span className="label">To</span>
          <input
            id={toId}
            type="date"
            className="input"
            style={{ minHeight: 30, width: 'auto' }}
            value={filters.to}
            min={filters.from || undefined}
            onChange={(e) => set({ to: e.target.value })}
          />
        </label>
        {filtered ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFilters({ kind: '', from: '', to: '' })}>
            Clear filters
          </button>
        ) : null}
      </div>
      <ErrorNotice error={billingError(csv.error)} title="Could not download the statement" />
      {first.loading && !first.data ? (
        <Loading label="Loading statement" />
      ) : first.error ? (
        <div className="panel-body">
          <ErrorNotice error={billingError(first.error)} title="Could not load the statement" />
        </div>
      ) : items.length ? (
        <>
          <LedgerTable entries={items} caption="Statement lines" />
          {cursor ? (
            <div className="panel-body stack-sm" style={{ alignItems: 'center' }}>
              <ErrorNotice error={more.error} />
              <button
                type="button"
                className="btn btn-sm"
                disabled={more.pending}
                onClick={async () => {
                  const page = await more.run(cursor);
                  if (page) setExtra({ key, items: [...(mine?.items ?? []), ...page.items], cursor: page.next_cursor });
                }}
              >
                {more.pending ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : null}
        </>
      ) : filtered ? (
        <Empty title="Nothing matches these filters">Try another kind or a wider date range.</Empty>
      ) : (
        <Empty title="No charges yet">A lead fee appears here when you accept a lead; credits and payments appear here too.</Empty>
      )}
    </section>
  );
}

// ── Invoices ─────────────────────────────────────────────────────────────
function Invoices() {
  const { api } = useSession();
  const list = useQuery(() => api.billingInvoices(), ['billing-invoices']);
  const items = list.data?.items ?? [];
  return (
    <section className="panel" aria-labelledby="billing-invoices-h">
      <div className="panel-head">
        <h2 id="billing-invoices-h">Invoices</h2>
        <span className="small muted">Monthly, net 15</span>
      </div>
      {list.loading && !list.data ? (
        <Loading label="Loading invoices" />
      ) : list.error ? (
        <div className="panel-body">
          <ErrorNotice error={billingError(list.error)} title="Could not load invoices" />
        </div>
      ) : items.length ? (
        <ul className="stack-sm" style={{ listStyle: 'none', margin: 0, padding: 'var(--s4) var(--s5)' }}>
          {items.map((inv) => (
            <InvoiceRow key={inv.id} invoice={inv} />
          ))}
        </ul>
      ) : (
        <Empty title="No invoices yet">Your FinderlyFix contact sends an invoice each month there is something to pay.</Empty>
      )}
    </section>
  );
}

function InvoiceRow({ invoice }: { invoice: InvoiceView }) {
  return (
    <li className="stack-xs" style={{ borderBottom: '1px solid var(--line)', paddingBottom: 'var(--s3)' }}>
      <span className="row-between">
        <span className="row" style={{ gap: 8 }}>
          <strong>{invoiceName(invoice)}</strong>
          <InvoiceStatusTag status={invoice.status} />
        </span>
        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCents(invoice.total_cents)}</strong>
      </span>
      <span className="small muted">
        {periodLabel(invoice)}
        {invoice.status === 'paid' && invoice.paid_at ? ` · paid ${formatDate(invoice.paid_at)}` : null}
        {invoice.status === 'issued' && invoice.due_at ? ` · due ${formatDate(invoice.due_at)}` : null}
      </span>
      <Disclosure label={<>Lines<span className="visually-hidden"> of {invoiceName(invoice)}, {periodLabel(invoice)}</span></>}>
        <InvoiceLines id={invoice.id} />
      </Disclosure>
    </li>
  );
}

function InvoiceLines({ id }: { id: string }) {
  const { api } = useSession();
  const detail = useQuery(() => api.billingInvoice(id), ['billing-invoice', id]);
  if (detail.loading && !detail.data) return <Loading label="Loading invoice" />;
  if (detail.error || !detail.data) return <ErrorNotice error={billingError(detail.error)} title="Could not load this invoice" />;
  if (!detail.data.entries.length) return <p className="small muted">No lines on this invoice.</p>;
  return <LedgerTable entries={detail.data.entries} caption={`Lines of ${invoiceName(detail.data)}`} />;
}

// ── Billing contact ──────────────────────────────────────────────────────
const ADDRESS_FIELDS = [
  { key: 'line1', label: 'Address', autoComplete: 'address-line1' },
  { key: 'line2', label: 'Address line 2', autoComplete: 'address-line2', optional: true },
  { key: 'city', label: 'City', autoComplete: 'address-level2' },
  { key: 'state', label: 'State', autoComplete: 'address-level1' },
  { key: 'postal_code', label: 'ZIP code', autoComplete: 'postal-code' },
] as const;
type AddressKey = (typeof ADDRESS_FIELDS)[number]['key'];

interface ContactForm {
  billing_email: string;
  legal_name: string;
  address: Record<AddressKey, string>;
}

function formFrom(account: BillingAccountView | null): ContactForm {
  const a = (account?.billing_address ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    billing_email: account?.billing_email ?? '',
    legal_name: account?.legal_name ?? '',
    address: { line1: str(a.line1), line2: str(a.line2), city: str(a.city), state: str(a.state), postal_code: str(a.postal_code) },
  };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function BillingContact({ account, onSaved }: { account: BillingAccountView | null; onSaved: (account: BillingAccountView) => void }) {
  const { api, role } = useSession();
  const toast = useToast();
  const editable = can(role, 'billing');
  const [form, setForm] = useState<ContactForm>(() => formFrom(account));
  const saved = formFrom(account);
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  useUnsavedChangesGuard(dirty);
  const save = useMutation(() => {
    const address = Object.fromEntries(
      Object.entries(form.address)
        .map(([k, v]) => [k, v.trim()] as const)
        .filter(([, v]) => v),
    );
    return api.updateBillingContact({
      billing_email: form.billing_email.trim() || null,
      legal_name: form.legal_name.trim() || null,
      billing_address: Object.keys(address).length ? address : null,
    });
  });
  const emailIssue = form.billing_email.trim() && !EMAIL_RE.test(form.billing_email.trim()) ? [{ path: 'billing_email', message: 'Enter an email address like billing@example.com.' }] : [];

  if (!account) {
    return (
      <section className="panel panel-pad stack-sm" aria-labelledby="billing-contact-h">
        <h2 id="billing-contact-h" className="h-sub">
          Billing contact
        </h2>
        <p className="small muted" style={{ margin: 0 }}>
          You can add who invoices go to once your billing account is set up. Accepting the fee schedule sets it up.
        </p>
      </section>
    );
  }

  return (
    <section className="panel" aria-labelledby="billing-contact-h">
      <div className="panel-head">
        <h2 id="billing-contact-h">Billing contact</h2>
      </div>
      <form
        className="panel-body stack-sm"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (emailIssue.length) return;
          const next = await save.run();
          if (!next) return;
          setForm(formFrom(next));
          onSaved(next);
          toast('Billing contact saved');
        }}
      >
        <p className="small muted" style={{ margin: 0 }}>
          Where your FinderlyFix contact sends invoices, and the name and address they carry.
        </p>
        <Field label="Billing email" issues={emailIssue}>
          {(p) => (
            <input
              {...p}
              className="input"
              type="email"
              autoComplete="email"
              value={form.billing_email}
              disabled={!editable}
              onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
            />
          )}
        </Field>
        <Field label="Legal name" hint="The business name invoices are made out to.">
          {(p) => (
            <input
              {...p}
              className="input"
              autoComplete="organization"
              value={form.legal_name}
              disabled={!editable}
              onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
            />
          )}
        </Field>
        {ADDRESS_FIELDS.map((f) => (
          <Field key={f.key} label={f.label} optional={'optional' in f && f.optional}>
            {(p) => (
              <input
                {...p}
                className="input"
                autoComplete={f.autoComplete}
                value={form.address[f.key]}
                disabled={!editable}
                onChange={(e) => setForm({ ...form, address: { ...form.address, [f.key]: e.target.value } })}
              />
            )}
          </Field>
        ))}
        <ErrorNotice error={billingError(save.error)} />
        {editable ? (
          <div>
            <button type="submit" className="btn btn-primary" disabled={!dirty || save.pending || emailIssue.length > 0}>
              {save.pending ? 'Saving…' : 'Save billing contact'}
            </button>
          </div>
        ) : (
          <p className="small muted">Only owners, admins and billing members can change these.</p>
        )}
      </form>
    </section>
  );
}
